// Part 1
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { disconnect } from "process";
import env from "../constants/env";
import { useDispatch } from "react-redux";
import Web3 from "web3";

// Part 2
export interface IssueInitialState {
  walletToggle: boolean;
  assetInputToggle: boolean;
  isVerify: boolean;
  address: string | null | undefined;
  assetAddress: string | null | undefined;
  assetChain: string;
  jwtToken: string;
  selectedAssetInfo: {
    token_id: string | null | undefined;
    token_address: string | null | undefined;
    token_metadata: any | null | undefined;
    proxy_token_exists: boolean;
  };
}
const initialState: IssueInitialState = {
  walletToggle: false,
  assetInputToggle: false,
  isVerify: false,
  address: null,
  assetAddress: null,
  assetChain: "",
  jwtToken: "",
  selectedAssetInfo: {
    token_id: null,
    token_address: null,
    token_metadata: null,
    proxy_token_exists: false,
  },
};

declare global {
  interface Window {
    ethereum?: any;
  }
}

const ConnectWallet = async () => {
  // Check if MetaMask is available
  if (window?.ethereum && typeof window.ethereum !== "undefined") {
    // Enable Ethereum provider
    try {
      await window.ethereum.enable();
      const web3 = new Web3(window.ethereum);
      const accounts = await web3.eth.getAccounts();

      const signatureMessage =
        "Sign this message to prove you own this address";

      const signature = await window.ethereum.request({
        method: "personal_sign",
        params: [signatureMessage, accounts[0]],
      });

      try {
        const jwtToken = await axios.post(`${env.USER_URL}/users/login`, {
          public_address: accounts[0],
          signature: signature,
          signature_message: signatureMessage,
        });
        return { account: accounts[0], jwtToken: jwtToken.data.accessToken };
      } catch (e) {
        console.error(e);
        alert("Error logging in, please try again after sometime");
      }
    } catch (error) {
      console.error("Error connecting to MetaMask:", error);
      alert("Error connecting to metamask, please try again after sometime");
    }
  } else {
    console.error("MetaMask not detected");
  }
};

// First, create the thunk
const initWalletConnect = createAsyncThunk("initConnectWallet", async () => {
  const response = await ConnectWallet();
  return response;
});

const walletDisconnect = createAsyncThunk("walletDisconnect", async () => {
  await window.ethereum.request({
    method: "eth_requestAccounts",
    params: [{ eth_accounts: {} }],
  });
  // clear local storage
  localStorage.clear();
  return null;
});

// Part 3
export const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {
    openWallet: (state) => {
      state.walletToggle = true;
    },
    openWalletVerify: (state) => {
      state.walletToggle = true;
      state.isVerify = true;
    },
    closeWallet: (state) => {
      state.walletToggle = false;
    },
    toggleWallet: (state) => {
      state.walletToggle = !state.walletToggle;
    },
    openAssetInput: (state) => {
      state.assetInputToggle = true;
    },
    closeAssetInput: (state) => {
      if (state.assetAddress && state.assetAddress.length > 0) {
        state.assetInputToggle = false;
      }
    },
    addSelectedAssetInfo: (
      state,
      action: PayloadAction<{
        token_id: string;
        token_address: string;
        token_metadata: any;
        proxy_token_exists: boolean;
      }>
    ) => {
      state.selectedAssetInfo = {
        token_id: action.payload.token_id,
        token_address: action.payload.token_address,
        token_metadata: action.payload.token_metadata,
        proxy_token_exists: action.payload.proxy_token_exists,
      }

    },
    addAssetAddress: (
      state,
      action: PayloadAction<{ assetAddress: string; assetChain: string }>
    ) => {
      if (
        action.payload &&
        action.payload.assetAddress.length > 0 &&
        action.payload.assetChain.length > 0
      ) {
        state.assetAddress = action.payload.assetAddress;
        state.assetChain = action.payload.assetChain;
        state.assetInputToggle = false;
      }
    },
  },
  extraReducers: (builder) => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder.addCase(initWalletConnect.fulfilled, (state, action) => {
      state.address = action.payload?.account;
      state.jwtToken = action.payload?.jwtToken;
      state.walletToggle = false;
      if (state.isVerify) {
        state.isVerify = false
      } else {
        state.assetInputToggle = true;
      }
    });
    builder.addCase(walletDisconnect.fulfilled, (state, action) => {
      state.address = null;
      state.walletToggle = false;
    });
  },
});

// Part 4
export const {
  openWallet,
  closeWallet,
  toggleWallet,
  openAssetInput,
  closeAssetInput,
  addAssetAddress,
  addSelectedAssetInfo,
  openWalletVerify,
} = walletSlice.actions;
export { initWalletConnect, walletDisconnect };
export default walletSlice.reducer;
